import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../common/Modal';
import Spinner from '../common/Spinner';
import { useUosMigration } from '../../hooks/useUosMigration';
import { getHttpStatus } from '../../api/client';
import {
  TOS_VERSION,
  enumerateAdmins,
  getAdminEnumeration,
  type AdminEnumerationAdmin,
} from '../../api/migration';
import { BRAND } from '../../utils/brand';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  subscriptionId: string;
  customHostname?: string | null;
  enumerationTimeoutMs?: number;
}

type Step = 'owner-selection' | 'local-only-warning' | 'confirmation';

export default function UosMigrationModal({
  isOpen,
  onClose,
  subscriptionId,
  customHostname,
  enumerationTimeoutMs = 10 * 60 * 1000,
}: Props) {
  const [step, setStep] = useState<Step>('owner-selection');
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [acknowledgmentAccepted, setAcknowledgmentAccepted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const migration = useUosMigration();
  const queryClient = useQueryClient();

  // Refs for focus management
  const hostnameInputRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  // Trigger enumeration when modal opens
  const enumerateMutation = useMutation({
    mutationFn: () => enumerateAdmins(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enumeration', subscriptionId] });
    },
  });

  // Timeout for enumeration (10 minutes). Declared before the poll query below
  // because refetchInterval reads it synchronously at observer construction.
  const [enumerationTimedOut, setEnumerationTimedOut] = useState(false);
  // Bumped on Retry so the timeout effect below re-arms for each attempt.
  const [enumerationAttempt, setEnumerationAttempt] = useState(0);

  // Poll for enumeration result
  const enumerationQuery = useQuery({
    queryKey: ['admin-enumeration', subscriptionId],
    queryFn: () => getAdminEnumeration(subscriptionId),
    // Keep polling until we reach a terminal outcome. Critically, a 404 here
    // means the enumeration was just triggered but isn't recorded yet — the
    // first-open race where this GET fires before POST /enumerate-admins has
    // inserted the pending action. Treat that as "still pending" and keep
    // polling, rather than stopping and dead-ending the modal.
    refetchInterval: (query) => {
      if (enumerationTimedOut) return false;
      if (query.state.data?.status === 'completed') return false;
      // Stop on a genuine HTTP failure (400 enumeration_failed, 500, ...),
      // but keep polling on 404 (not-recorded-yet) and on pending/running.
      const errorStatus = getHttpStatus(query.state.error);
      if (errorStatus != null && errorStatus !== 404) return false;
      return 2000;
    },
    // Keep polling when the tab is backgrounded — the default pauses the
    // interval on focus loss, which let the wall-clock timeout below fire
    // against a backend that had long since completed (p82, 2026-07-23).
    refetchIntervalInBackground: true,
    enabled: isOpen,
    retry: false,
  });
  const refetchEnumeration = enumerationQuery.refetch;

  // Arm the enumeration timeout while the modal is open, once per attempt.
  // The flag is cleared in handleClose alongside the rest of the modal state.
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      // Last-chance recheck before declaring failure: browsers throttle timers
      // in background tabs, so the poll may have missed a completed result.
      // Only time out if a fresh fetch still has nothing.
      void refetchEnumeration().then(({ data }) => {
        if (data?.status !== 'completed') setEnumerationTimedOut(true);
      });
    }, enumerationTimeoutMs);
    return () => clearTimeout(timer);
  }, [isOpen, enumerationAttempt, enumerationTimeoutMs, refetchEnumeration]);

  // Trigger enumeration on modal open
  useEffect(() => {
    if (isOpen && !enumerateMutation.isPending && !enumerateMutation.isSuccess) {
      enumerateMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const data = enumerationQuery.data;
  const admins = data?.status === 'completed' ? data?.data?.admins : undefined;
  const cloudAccess = data?.status === 'completed' ? data?.data?.cloud_access : undefined;
  // Failure is only meaningful when we have no admin list to show. This keeps
  // the SA-impersonation case working: the write-guarded POST /enumerate-admins
  // can fail (403) while the GET still serves a prior completed enumeration —
  // the list must win. When we have nothing, surface a retryable failure for:
  //   - the trigger POST failing outright (no action will ever be recorded), or
  //   - a non-404 HTTP error from the GET (400 enumeration_failed, 500), or
  //   - the 10-minute timeout.
  // A 404 is transient (enumeration triggered but not yet recorded) and keeps
  // polling rather than dead-ending the modal.
  const enumGetErrorStatus = getHttpStatus(enumerationQuery.error);
  const enumerationFailed =
    !admins &&
    (enumerateMutation.isError ||
      (enumGetErrorStatus != null && enumGetErrorStatus !== 404) ||
      enumerationTimedOut);
  // Show the "verifying" spinner whenever the modal is open and we have neither
  // the admin list nor a terminal failure. This covers pending/running AND the
  // first-open 404 race that previously left the modal in a dead state (owner
  // text + greyed Continue, no list, no spinner, no error).
  const isEnumerating = isOpen && !admins && !enumerationFailed;

  // Auto-select cloud owner if available. State-adjust during render (React's
  // documented pattern for deriving state from just-arrived data) — the
  // !selectedOwnerId guard makes this settle in one extra render.
  if (admins && cloudAccess?.cloud_owner_ubic_uuid && !selectedOwnerId) {
    const cloudOwner = admins.find(
      (a) => a.ubic_uuid === cloudAccess.cloud_owner_ubic_uuid && a.is_super_admin
    );
    if (cloudOwner) {
      setSelectedOwnerId(cloudOwner.admin_id);
    }
  }

  // Focus management on state changes
  useEffect(() => {
    if (migration.isSuccess && hostnameInputRef.current) {
      hostnameInputRef.current.focus();
      hostnameInputRef.current.select();
    }
  }, [migration.isSuccess]);

  useEffect(() => {
    if (migration.isError && errorRef.current) {
      errorRef.current.focus();
    }
  }, [migration.isError]);

  // Derive the new hostname from current hostname
  const newHostname = subscriptionId.endsWith('.cloudunifi.com')
    ? subscriptionId.replace('.cloudunifi.com', '.netconsole.cloud')
    : `${subscriptionId.replace(/\./g, '-')}.netconsole.cloud`;

  const selectedOwner = admins?.find(a => a.admin_id === selectedOwnerId);
  const selectedOwnerName = selectedOwner ? (selectedOwner.name || selectedOwner.email) : null;
  const canProceedToConfirmation = !!selectedOwnerId;
  const canSubmit = tosAccepted && acknowledgmentAccepted && !!selectedOwnerId;

  const handleSelectOwner = (admin: AdminEnumerationAdmin) => {
    setSelectedOwnerId(admin.admin_id);
  };

  const handleContinue = () => {
    if (!canProceedToConfirmation || !selectedOwner) return;
    if (!selectedOwner.has_cloud_sso) {
      setStep('local-only-warning');
    } else {
      setStep('confirmation');
    }
  };

  const handleBack = () => {
    setStep('owner-selection');
  };

  const handleMigrate = () => {
    if (!selectedOwnerId) return;
    migration.mutate({
      subscriptionId,
      tosVersion: TOS_VERSION,
      ownerAdminId: selectedOwnerId,
      acknowledgments: {
        tos_acceptance: tosAccepted,
        end_user_infrastructure_acknowledgment: acknowledgmentAccepted,
      },
    });
  };

  const handleClose = () => {
    setStep('owner-selection');
    setSelectedOwnerId(null);
    setTosAccepted(false);
    setAcknowledgmentAccepted(false);
    setCopied(false);
    setCopyError(false);
    setEnumerationTimedOut(false);
    // The modal stays mounted between opens. Drop the finished enumeration so
    // the open effect runs a fresh one next time (e.g. after the customer links
    // an admin to their Ubiquiti account).
    enumerateMutation.reset();
    queryClient.removeQueries({ queryKey: ['admin-enumeration', subscriptionId] });
    migration.reset();
    onClose();
  };

  const handleCopyHostname = async () => {
    if (!migration.data?.uos_hostname) return;

    try {
      await navigator.clipboard.writeText(migration.data.uos_hostname);
      setCopied(true);
      setCopyError(false);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard write failed:', err);
      setCopyError(true);
    }
  };

  const handleRetryEnumeration = () => {
    setEnumerationTimedOut(false);
    setEnumerationAttempt((n) => n + 1);
    enumerateMutation.reset();
    queryClient.removeQueries({ queryKey: ['admin-enumeration', subscriptionId] });
    enumerateMutation.mutate();
  };

  // Show success state after migration is initiated
  if (migration.isSuccess && migration.data?.uos_hostname) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Migration Started">
        <div className="space-y-4">
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            Migration complete. Your new hostname is {migration.data.uos_hostname}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Migration initiated successfully!
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Your UniFi OS instance is being provisioned. This process typically takes 20-30 minutes to complete.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <label htmlFor="uos-hostname" className="text-sm font-medium text-gray-700 mb-2 block">Your new hostname:</label>
            <div className="flex items-center gap-2">
              <input
                id="uos-hostname"
                ref={hostnameInputRef}
                type="text"
                readOnly
                value={migration.data.uos_hostname}
                className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-sm font-mono text-gray-900 select-all"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              {!copyError && (
                <button
                  onClick={handleCopyHostname}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                  aria-label={copied ? 'Hostname copied' : 'Copy hostname to clipboard'}
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-600">Copy</span>
                    </>
                  )}
                </button>
              )}
            </div>
            {copyError && (
              <p className="text-xs text-amber-600 mt-2" role="alert">
                Copy failed. Please select the hostname above and copy manually.
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              If you have CNAME records pointing to your old hostname, update them to point to this new hostname after the migration completes.
            </p>
          </div>

          <div className="pt-2">
            <button
              onClick={handleClose}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Step 1: Owner Selection
  if (step === 'owner-selection') {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Upgrade to UniFi OS Platform">
        <div className="space-y-4">
          {isEnumerating ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <Spinner size="sm" />
              <div>
                <p className="text-sm text-gray-600">Verifying your UniFi setup prior to migration...</p>
                <p className="text-xs text-gray-400 mt-1">This may take up to 5 minutes to complete.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-gray-600">
                Select which admin account will become the <strong>Owner</strong> of your new UniFi OS console.
              </p>
              <p className="text-sm text-gray-500">
                The Owner is the top-level administrator of a UniFi OS console. Each console has exactly one Owner at a time, who has unrestricted access to all console settings, applications, and user management — including the ability to add, modify, or remove other admins. Only the Owner can transfer ownership to another account.
              </p>
            </div>
          )}

          {/* Enumeration failed */}
          {enumerationFailed && (
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">
                  {enumerationTimedOut
                    ? 'Verification could not be completed. Please try again or contact support if the issue persists.'
                    : 'Failed to load admin accounts. Please try again.'}
                </p>
              </div>
              <button
                onClick={handleRetryEnumeration}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Admin list */}
          {admins && (
            <div className="space-y-2">
              {cloudAccess?.enabled && (
                <div className="px-3 py-2 bg-blue-50 rounded text-sm text-blue-800">
                  Cloud Remote Management is enabled on this controller.
                </div>
              )}

              <div className="space-y-2">
                {admins.map((admin: AdminEnumerationAdmin) => {
                  const eligible = admin.is_super_admin;
                  const selected = selectedOwnerId === admin.admin_id;
                  return (
                    <label
                      key={admin.admin_id}
                      className={`block border rounded-lg p-3 transition-colors ${
                        eligible
                          ? selected
                            ? 'border-blue-500 bg-blue-50 cursor-pointer'
                            : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                          : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="pt-0.5">
                          {eligible ? (
                            <input
                              type="radio"
                              name="owner"
                              value={admin.admin_id}
                              checked={selected}
                              onChange={() => handleSelectOwner(admin)}
                              className="text-blue-600"
                            />
                          ) : (
                            <input type="radio" disabled className="opacity-30" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900 text-sm">{admin.name || '-'}</span>
                            {admin.is_super_admin ? (
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                                Super Admin
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                                {admin.site_privileges.map((p) => p.role).join(', ') || 'Limited'}
                              </span>
                            )}
                            {admin.has_cloud_sso ? (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                Cloud SSO
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                                Local only
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">{admin.email || '-'}</p>
                          {!eligible && (
                            <p className="text-xs text-gray-400 mt-0.5">Only super admins can be the Owner</p>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            {!isEnumerating && (
              <button
                onClick={handleContinue}
                disabled={!canProceedToConfirmation}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  if (step === 'local-only-warning') {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Upgrade to UniFi OS Platform">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <svg
              className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
            <div className="text-sm text-amber-900 space-y-2">
              <p className="font-medium">This admin is not linked to a Ubiquiti cloud account.</p>
              <p>
                After migration, the only way to log in will be the local username
                and password from your existing controller — Ubiquiti email + SSO
                login will not work for this owner.
              </p>
              <p>
                If you want SSO login, either choose an admin with a linked
                Ubiquiti account, or accept this admin's invite at{' '}
                <a
                  href="https://account.ui.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-amber-700"
                >
                  account.ui.com
                </a>{' '}
                first, then close this window and start the upgrade again.
              </p>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => setStep('owner-selection')}
              autoFocus
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Choose Different Admin
            </button>
            <button
              type="button"
              onClick={() => setStep('confirmation')}
              className="px-4 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700"
            >
              Continue with Local-Only Owner
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Step 2: Confirmation (TOS + acknowledgments)
  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Confirm Migration">
      <div className="space-y-4">
        {/* Live region for screen reader announcements */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {migration.isPending && 'Migration in progress. Please wait.'}
          {migration.isError && 'Migration failed. Please try again.'}
        </div>

        {/* Selected owner summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <p className="text-sm text-gray-700">
            <strong>Selected Owner:</strong> {selectedOwnerName}
          </p>
          <button
            onClick={handleBack}
            disabled={migration.isPending}
            className="text-sm text-blue-600 hover:text-blue-700 mt-1"
          >
            Change selection
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-3">
          <h4 className="font-medium text-blue-900">What to expect:</h4>
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">1.</span>
              <span>Your controller configuration and devices will be migrated automatically</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">2.</span>
              <span>Your new UniFi OS will be hosted at <strong className="font-mono">{newHostname}</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">3.</span>
              <span>Devices will reconnect to the new controller automatically</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">4.</span>
              <span>User accounts from your current controller will be migrated to UniFi OS</span>
            </li>
          </ul>
        </div>

        <div className="space-y-3 py-2">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="tos-acceptance"
              checked={tosAccepted}
              onChange={(e) => setTosAccepted(e.target.checked)}
              aria-describedby="tos-description"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <label htmlFor="tos-acceptance" className="text-sm text-gray-700 cursor-pointer">
              I accept the{' '}
              <a
                href={BRAND.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                {BRAND.name} Terms of Service
              </a>
              <span id="tos-description" className="sr-only">
                You must accept the Terms of Service to proceed with the migration.
              </span>
            </label>
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="infrastructure-acknowledgment"
              checked={acknowledgmentAccepted}
              onChange={(e) => setAcknowledgmentAccepted(e.target.checked)}
              aria-describedby="acknowledgment-description"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <label htmlFor="infrastructure-acknowledgment" className="text-sm text-gray-700 cursor-pointer">
              I acknowledge that I am the end user of any third-party software
              installed on this infrastructure and that service fees are for managed
              infrastructure only
              <span id="acknowledgment-description" className="sr-only">
                You must acknowledge that you are the end user of third-party software.
              </span>
            </label>
          </div>
        </div>

        {customHostname && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h4 className="font-medium text-amber-800">Custom Domain Migration Required</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Your current custom domain: <strong>{customHostname}</strong>
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  After migration completes, you'll need to update your DNS CNAME record to point to your new UOS hostname. We'll guide you through this once your service is ready.
                </p>
              </div>
            </div>
          </div>
        )}

        {migration.isError && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            className="bg-red-50 border border-red-200 rounded-md p-3"
          >
            <p className="text-sm text-red-800">
              {migration.error instanceof Error
                ? migration.error.message
                : 'Failed to start migration. Please try again or contact support.'}
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={handleBack}
            disabled={migration.isPending}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
          >
            Back
          </button>
          <button
            onClick={handleMigrate}
            disabled={!canSubmit || migration.isPending}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {migration.isPending ? (
              <>
                <Spinner size="sm" className="border-white border-t-transparent" />
                <span>Processing...</span>
              </>
            ) : (
              'Accept & Upgrade'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
